// src/components/PrivacyPolicy.jsx
import React from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, Shield, ExternalLink } from 'lucide-react';

const PrivacyPolicy = () => {
  const navigate = useNavigate();

  return (
    <div className="privacy-policy-page">
      <div className="privacy-header">
        <button 
          className="button button-back"
          onClick={() => navigate(-1)}
        >
          <ArrowLeft size={18} />
          Back
        </button>
      </div>

      <div className="privacy-content">
        <div className="privacy-icon">
          <Shield size={48} />
        </div>
        
        <h1>Privacy Policy</h1>
        <p className="privacy-subtitle">
          Last updated: December 2024
        </p>

        <section className="privacy-section">
          <h2>Our Commitment to Privacy</h2>
          <p>
            The Gov Glance Foundation deeply values your privacy. We believe that 
            civic engagement and government transparency should not come at the cost 
            of your personal data. Govsky is designed with privacy as a core principle.
          </p>
        </section>

        <section className="privacy-section">
          <h2>How Govsky Works</h2>
          <p>
            Govsky is a <strong>client application</strong> for the Bluesky social network 
            and AT Protocol (Authenticated Transfer Protocol). This means Govsky is a 
            window into the Bluesky network—we don't run our own social network or 
            store your posts and social data.
          </p>
          <p>
            When you use Govsky, you're connecting directly to Bluesky's servers using 
            their official API. Your posts, likes, follows, and other social interactions 
            are handled by Bluesky, not by us.
          </p>
        </section>

        <section className="privacy-section">
          <h2>Your Data Stays on Your Device</h2>
          <p>
            <strong>We do not upload your personal information to our servers.</strong> All 
            personal data, including:
          </p>
          <ul>
            <li>Your login credentials (if you choose to save them)</li>
            <li>Your saved/bookmarked posts</li>
            <li>Your country preference</li>
            <li>Your session information</li>
          </ul>
          <p>
            ...is stored <strong>locally on your device</strong> using your browser's 
            local storage. This data never leaves your device and is not transmitted 
            to Gov Glance servers.
          </p>
        </section>

        <section className="privacy-section">
          <h2>Bluesky & AT Protocol</h2>
          <p>
            Bluesky is a decentralized social network built on the AT Protocol. Unlike 
            traditional social networks, AT Protocol is designed to give users more 
            control over their data and identity.
          </p>
          <p>
            When you log in through Govsky, you're authenticating with Bluesky's servers. 
            Any data you share publicly on Bluesky (posts, profile information, etc.) is 
            governed by Bluesky's privacy practices.
          </p>
          <p>
            <strong>Bluesky is responsible for:</strong>
          </p>
          <ul>
            <li>Storing and managing your account</li>
            <li>Processing your posts and social interactions</li>
            <li>Managing your public and private data on their network</li>
            <li>Their own data retention and privacy practices</li>
          </ul>
          <p>
            We encourage you to review Bluesky's privacy policy:
          </p>
          <a 
            href="https://bsky.social/about/support/privacy-policy"
            target="_blank"
            rel="noopener noreferrer"
            className="privacy-link"
          >
            <ExternalLink size={16} />
            Bluesky Privacy Policy
          </a>
        </section>

        <section className="privacy-section">
          <h2>Analytics We Collect</h2>
          <p>
            Govsky collects only <strong>basic, anonymous analytics</strong> to help us 
            understand how the app is being used and improve the experience. This includes:
          </p>
          <ul>
            <li>Page views and general usage patterns</li>
            <li>Anonymous crash reports</li>
            <li>General geographic region (country-level only)</li>
          </ul>
          <p>
            This analytics data is <strong>anonymous and aggregated</strong>. We do not 
            track individual users, collect personal identifiers, or link analytics data 
            to your Bluesky account.
          </p>
        </section>

        <section className="privacy-section">
          <h2>No Advertising or Data Sales</h2>
          <p>
            Govsky does not display advertisements and we <strong>never sell, share, or 
            monetize your personal data</strong>. The Gov Glance Foundation is a 
            non-profit organization committed to civic technology and government 
            transparency—not data harvesting.
          </p>
        </section>

        <section className="privacy-section">
          <h2>Age Requirement</h2>
          <p>
            Govsky is intended for users who are <strong>16 years of age or older</strong>. 
            By using Govsky, you confirm that you meet this age requirement. This aligns 
            with Bluesky's terms of service.
          </p>
        </section>

        <section className="privacy-section">
          <h2>Changes to This Policy</h2>
          <p>
            We may update this privacy policy from time to time. Any changes will be 
            posted on this page with an updated revision date.
          </p>
        </section>

        <section className="privacy-section">
          <h2>Contact Us</h2>
          <p>
            If you have any questions about this privacy policy or our practices, 
            please contact us at:
          </p>
          <a 
            href="https://govglance.org"
            target="_blank"
            rel="noopener noreferrer"
            className="privacy-link"
          >
            <ExternalLink size={16} />
            govglance.org
          </a>
        </section>

        <div className="privacy-footer">
          <p>© {new Date().getFullYear()} Gov Glance Foundation. All rights reserved.</p>
        </div>
      </div>
    </div>
  );
};

export default PrivacyPolicy;

